import styles from 'css/src/components/codeo/CostSummary.module.scss';

import React, { useEffect, useMemo, useRef, useState } from 'react';

import { ApiService } from 'js/src/client';
import type { UsageCostResponse } from 'js/src/client';
import type { Usage } from 'js/src/agent/types';
import Tooltip from 'js/src/components/common/ui/Tooltip';

const fmtTokens = (n: number) => n.toLocaleString();

export interface UsageCostPart {
    usage: Usage;
    model?: string;
}

// One "<label> (<tokens> tok)  <cost>" row in the hover breakdown. Rendered only when there are
// tokens. Each side stays on one line so the two columns don't wrap or collide.
function BreakdownRow({ label, tokens, cost }: { label: string; tokens: number; cost: string }) {
    if (tokens <= 0) return null;
    return (
        <div className="d-flex justify-content-between" style={{ gap: 24, whiteSpace: 'nowrap' }}>
            <span>
                {label} ({fmtTokens(tokens)} tok)
            </span>
            <span>{cost}</span>
        </div>
    );
}

function CostBreakdown({ cost, parts }: { cost: UsageCostResponse; parts: UsageCostPart[] }) {
    const totals = parts.reduce(
        (acc, { usage }) => ({
            inputTokens: acc.inputTokens + usage.input_tokens,
            cacheReadTokens: acc.cacheReadTokens + (usage.cache_read_input_tokens ?? 0),
            cacheWriteTokens: acc.cacheWriteTokens + (usage.cache_creation_input_tokens ?? 0),
            outputTokens: acc.outputTokens + usage.output_tokens,
        }),
        { inputTokens: 0, cacheReadTokens: 0, cacheWriteTokens: 0, outputTokens: 0 },
    );

    const usd = (n: number) => `$${n.toFixed(n < 0.01 ? 4 : 3)}`;

    return (
        <div style={{ fontSize: 12, textAlign: 'left' }}>
            <div
                className="d-flex justify-content-between"
                style={{ gap: 24, fontWeight: 600, marginBottom: 4, whiteSpace: 'nowrap' }}
            >
                <span>Total</span>
                <span>{cost.formatted_total}</span>
            </div>
            <BreakdownRow label="Input" tokens={totals.inputTokens} cost={usd(cost.input)} />
            <BreakdownRow
                label="Cached input"
                tokens={totals.cacheReadTokens}
                cost={usd(cost.cache_read)}
            />
            <BreakdownRow
                label="Cache write"
                tokens={totals.cacheWriteTokens}
                cost={usd(cost.cache_write)}
            />
            <BreakdownRow label="Output" tokens={totals.outputTokens} cost={usd(cost.output)} />
        </div>
    );
}

// Parts without a model are dropped: the endpoint prices per model and rejects one it has
// no rate for, so there is nothing meaningful to ask for here.
function toRequestParts(parts: UsageCostPart[]) {
    return parts.flatMap(({ usage, model }) =>
        model ? [{ model, usage: toRequestUsage(usage) }] : [],
    );
}

function toRequestUsage(usage: Usage) {
    return {
        input_tokens: usage.input_tokens,
        output_tokens: usage.output_tokens,
        cache_read_tokens: usage.cache_read_input_tokens ?? 0,
        cache_write_tokens: usage.cache_creation_input_tokens ?? 0,
        cache_write_1h_tokens: usage.cache_creation_1h_input_tokens ?? 0,
    };
}

const hasUsage = (parts: ReturnType<typeof toRequestParts>) =>
    parts.some(({ usage }) => Object.values(usage).some((tokens) => tokens > 0));

/**
 * Displays the cost of some token usage, priced by the backend.
 *
 * Rates live in `openai_gpt/pricing.py`; this component never computes a dollar figure
 * itself, so the two sides can't drift.
 *
 * Pass one part per model so multi-model flows (e.g. codeo's Opus conversation + its Sonnet
 * brand analyzer) price each piece at its own rate before summing. By default a zero-cost
 * turn renders nothing; pass showWhenZero to keep the "$0 total" line visible (e.g. the chat
 * input footer).
 */
export function CostSummary({
    parts,
    showWhenZero = false,
    showTotalLabel = true,
}: {
    parts: UsageCostPart[];
    showWhenZero?: boolean;
    // Whether to append the word "total" after the amount. The full breakdown is
    // always available on hover; callers with a tight footer can hide the label.
    showTotalLabel?: boolean;
}) {
    const [cost, setCost] = useState<UsageCostResponse | null>(null);

    const requestParts = useMemo(() => toRequestParts(parts), [parts]);
    // Keyed on the token numbers themselves: the footer re-renders on every streamed delta,
    // and refetching for identical usage would hammer the endpoint.
    const requestKey = JSON.stringify(requestParts);

    // Guards against a slow response for stale usage overwriting a newer one.
    const latestKey = useRef(requestKey);
    latestKey.current = requestKey;

    useEffect(() => {
        const sent = JSON.parse(requestKey) as ReturnType<typeof toRequestParts>;
        if (!hasUsage(sent)) {
            setCost(null);
            return;
        }

        let cancelled = false;
        ApiService.tokensCostCreate({ body: { parts: sent } })
            .then(({ data }) => {
                if (!cancelled && latestKey.current === requestKey) setCost(data);
            })
            .catch(() => {
                // A pricing failure must never take down the surface it annotates: drop the
                // cost line rather than surfacing an error next to the user's conversation.
                if (!cancelled) setCost(null);
            });

        return () => {
            cancelled = true;
        };
    }, [requestKey]);

    // No usage yet means nothing to price and no call worth making. The footer still wants
    // its "$0" placeholder, so render that locally rather than round-tripping for a zero.
    if (!cost) {
        if (!showWhenZero) return null;
        return (
            <div
                style={{ fontSize: 12, color: '#475569', cursor: 'default', width: 'fit-content' }}
            >
                <span style={{ fontWeight: 600 }}>$0</span>
                {showTotalLabel ? ' total' : ''}
            </div>
        );
    }
    if (cost.total <= 0 && !showWhenZero) return null;

    return (
        <Tooltip
            title={<CostBreakdown cost={cost} parts={parts} />}
            className={styles['cost-breakdown-tooltip']}
        >
            <div
                style={{ fontSize: 12, color: '#475569', cursor: 'default', width: 'fit-content' }}
            >
                <span style={{ fontWeight: 600 }}>{cost.formatted_total}</span>
                {showTotalLabel ? ' total' : ''}
            </div>
        </Tooltip>
    );
}
